import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@repo/ui/src/components/ui/button';
import type { SessionWithDetail } from '@repo/shared-types';
import type { PutSessionNotesInput } from '@repo/validation';
import { useSessionNotes, useSaveSessionNotes } from '../../hooks/useProgress';
import { fmtDayLabel } from '../../lib/sessionTime';
import { ResponsiveSheet, useResponsiveSheetClose } from '../ResponsiveSheet';
import { SessionNotesForm, type NotesRoster } from './SessionNotesForm';
import { anyUploading, draftSignature, hasAnyHomework, hasAnyText, photoCount, type DraftPhoto, type NoteDraft } from './noteDraft';
import { useSubscription } from '../../hooks/useSubscription';
import type { OrphanItem } from './NoteItemTable';
import { TemplateHintLine } from './TemplateHintLine';
import type { HomeworkPhotoRead, NoteItem, NoteTemplate, NoteTemplateItem } from '@repo/shared-types';

function emptyDraft(): NoteDraft {
  return { comment: '', homework: '', items: {}, homeworkItems: {}, photos: [], rating: null };
}

/** Stored photos → draft photos: ready, saved, previewed by their signed url. */
const toDraftPhotos = (photos: HomeworkPhotoRead[] | undefined): DraftPhoto[] =>
  (photos ?? []).map((p) => ({ id: p.id, key: p.key, width: p.width, height: p.height, bytes: p.bytes, status: 'ready', progress: 100, previewUrl: p.url, saved: true }));

/** Draft photos → wire: only the uploaded ones, and only the stored shape. */
const toWirePhotos = (photos: DraftPhoto[]) =>
  photos.filter((p) => p.status === 'ready').map((p) => ({ id: p.id, key: p.key, width: p.width, height: p.height, bytes: p.bytes }));

const toRecord = (items: NoteItem[] | undefined): Record<string, string> =>
  Object.fromEntries((items ?? []).map((i) => [i.itemId, i.content]));

/** Saved items whose template row no longer exists (renamed/deleted since):
 *  rendered read-only with the snapshotted name, never dropped silently. */
const orphansOf = (items: NoteItem[] | undefined, current: NoteTemplateItem[]): OrphanItem[] => {
  const known = new Set(current.map((i) => i.id));
  return (items ?? []).filter((i) => !known.has(i.itemId)).map((i) => ({ itemId: i.itemId, name: i.name }));
};

/** Record → wire items. The NAME sent is the template's current name for a
 *  live item, or the saved snapshot for an orphan (the server stores the name
 *  as sent: that is the snapshot rule). Blank values are dropped here too. */
const toWireItems = (values: Record<string, string>, current: NoteTemplateItem[], orphans: OrphanItem[]) => {
  const names = new Map<string, string>([...current.map((i) => [i.id, i.name] as const), ...orphans.map((o) => [o.itemId, o.name] as const)]);
  return Object.entries(values)
    .filter(([, v]) => v.trim())
    .map(([itemId, content]) => ({ itemId, name: names.get(itemId) ?? itemId, content }))
    .filter((i) => names.has(i.itemId));
};

/**
 * A separate component (not inlined into the footer JSX in
 * `SessionNotesPanel`) because `useResponsiveSheetClose()` must be called
 * from a component that renders as a DESCENDANT of `ResponsiveSheet`'s
 * `ResponsiveSheetCloseContext.Provider` — which wraps `footer` INSIDE
 * `ResponsiveSheet`'s own render, one level below `SessionNotesPanel` itself.
 * Calling the hook directly in `SessionNotesPanel`'s body would look for a
 * Provider among ITS ancestors (there is none there) and throw; a small
 * child component whose JSX ends up slotted into `footer` is what actually
 * gets mounted below the Provider, regardless of where the JSX was written.
 */
function NotesFooterCancelButton({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation('progress');
  const { requestClose } = useResponsiveSheetClose();
  return (
    <Button variant="ghost" className="h-[44px]" onClick={requestClose} disabled={disabled}>
      {t('notes.cancel')}
    </Button>
  );
}

/**
 * D1 — the session-note form, ported onto `ResponsiveSheet` (a vaul Drawer on
 * phone, a right Sheet on desktop; same body either way, `SessionNotesForm`).
 * See docs/design/design_handoff_progress_tracking/18-port-guide.md §2 and
 * ResponsiveSheet.tsx's own header comment for why the Sheet/Drawer choice is
 * latched for the duration of one open.
 *
 * This component is the ONE state owner for the draft text (`classDraft`,
 * `students`, `openStudentId`) and for the dirty-close guard (`dirty`,
 * `discard`, passed down to `ResponsiveSheet`, which owns the confirm dialog
 * itself). That draft state used to live in `SessionNotesForm`, which broke
 * the moment `useIsMobile()` resolved after mount and flipped which container
 * wrapped the form: React tears down an entire subtree when its root element
 * type changes, so a Form instance nested inside the outgoing container was
 * destroyed along with whatever the teacher had already typed. Hooks belong
 * to THIS component's fiber, not to whichever child tree `ResponsiveSheet`
 * currently renders, so keeping the draft here survives that swap.
 */
export function SessionNotesPanel({
  session,
  open,
  onOpenChange,
  focusStudentId,
}: {
  session: SessionWithDetail;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  focusStudentId?: string | null;
}) {
  const { t } = useTranslation('progress');
  const { data: notes } = useSessionNotes(open ? session.id : null);
  const save = useSaveSessionNotes(session.id);

  // Recomputed only when the participants array identity changes — a plain
  // `.map` here would hand SessionNotesForm a fresh array (and fresh row
  // objects) on every render for no reason.
  const roster: NotesRoster[] = useMemo(
    () =>
      session.participants.map((p) => ({
        studentId: p.studentId,
        studentName: p.studentName,
        studentNickname: p.studentNickname ?? null,
        attendance: p.attendance,
      })),
    [session.participants],
  );

  // Roster SIZE, never the present count (handoff §7.4) — a 1-1 lesson has
  // no class tier at all, its content goes into that student's personal row.
  const showClassTier = roster.length > 1;

  const [classDraft, setClassDraft] = useState<NoteDraft>(emptyDraft);
  const [students, setStudents] = useState<Record<string, NoteDraft>>({});
  const [openStudentId, setOpenStudentId] = useState<string | null>(null);
  const focusRef = useRef<HTMLTextAreaElement | null>(null);

  const template: NoteTemplate | null = notes?.template ?? null;
  const baseline = useMemo(() => {
    const byStudent: Record<string, NoteDraft> = {};
    const orphansByStudent: Record<string, OrphanItem[]> = {};
    const homeworkOrphansByStudent: Record<string, OrphanItem[]> = {};
    for (const s of roster) byStudent[s.studentId] = emptyDraft();
    for (const n of notes?.studentNotes ?? []) {
      byStudent[n.studentId] = {
        comment: n.comment ?? '', homework: n.homework ?? '',
        items: toRecord(n.items), homeworkItems: toRecord(n.homeworkItems),
        photos: toDraftPhotos(n.homeworkPhotos),
        rating: n.rating,
      };
      orphansByStudent[n.studentId] = orphansOf(n.items, template?.studentItems ?? []);
      homeworkOrphansByStudent[n.studentId] = orphansOf(n.homeworkItems, template?.homeworkItems ?? []);
    }
    return {
      classDraft: {
        comment: notes?.classNote?.comment ?? '', homework: notes?.classNote?.homework ?? '',
        items: toRecord(notes?.classNote?.items), homeworkItems: toRecord(notes?.classNote?.homeworkItems),
        photos: toDraftPhotos(notes?.classNote?.homeworkPhotos),
        rating: null,
      },
      byStudent,
      orphansByStudent,
      homeworkOrphansByStudent,
      classOrphans: orphansOf(notes?.classNote?.items, template?.classItems ?? []),
      // Every tier gets the orphan treatment: a wire list built from the
      // current template alone would drop content written under a since-
      // removed item, and the server treats a missing item as cleared
      // (review C1).
      classHomeworkOrphans: orphansOf(notes?.classNote?.homeworkItems, template?.homeworkItems ?? []),
    };
  }, [roster, notes, template]);

  // Hydrate the draft from `notes` exactly once per open cycle — guarded by
  // a ref, not just the `open`/`notes` deps, so a background refetch of
  // `notes` while the teacher is mid-edit can never stomp what they just
  // typed. Resets when the panel closes, so the NEXT open re-baselines from
  // whatever is actually saved — this is also what makes discarding an edit
  // and reopening show the true saved text rather than the discarded draft.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hydratedRef.current = false;
      return;
    }
    if (!notes || hydratedRef.current) return;
    // The setState here is intentional — the query resolving (or the panel
    // opening) is the external signal we react to: a one-time hydration into
    // editable draft state, not a per-render derivation (which would stomp
    // the teacher's own edits on every subsequent render).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setClassDraft(baseline.classDraft);
    setStudents(baseline.byStudent);
    setOpenStudentId(focusStudentId ?? null);
    hydratedRef.current = true;
  }, [open, notes, baseline, focusStudentId]);

  // Opening from the per-student icon on the attendance card — including
  // switching to a DIFFERENT student while the drawer is already open.
  // requestAnimationFrame so focus lands AFTER the panel has mounted: vaul
  // animates ~300ms and an early focus scroll would drag the drawer (port
  // guide §2.3).
  useEffect(() => {
    if (!focusStudentId) return;
    // The setState here is intentional — a new `focusStudentId` prop (a
    // caller asking to open a specific student) is the external signal;
    // this effect also has to perform an imperative focus() a frame later,
    // which cannot happen during render at all.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpenStudentId(focusStudentId);
    const raf = requestAnimationFrame(() => focusRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [focusStudentId]);

  // Whole-draft compare: four fields per tier, so a typed item is dirty too.
  // `draftSignature` projects photos to id+key so an upload's progress tick
  // or a preview URL never reads as an edit (THI-196).
  const dirty =
    draftSignature(classDraft) !== draftSignature(baseline.classDraft) ||
    roster.some(
      (s) => draftSignature(students[s.studentId] ?? emptyDraft()) !== draftSignature(baseline.byStudent[s.studentId] ?? emptyDraft()),
    );
  const uploading = anyUploading(classDraft) || roster.some((s) => anyUploading(students[s.studentId]));
  const totalPhotos = photoCount(classDraft) + roster.reduce((n, s) => n + photoCount(students[s.studentId]), 0);

  // Three-state plan gate for the "Thêm ảnh" button (§4.4): undefined while
  // the subscription loads, so a paying org never flashes the locked look.
  const { data: subscription, isPending: subscriptionPending } = useSubscription();
  const planAllowsPhotos = subscriptionPending ? undefined : subscription?.plan.allowsProgressLink === true;

  const writtenCount = roster.filter((s) => hasAnyText(students[s.studentId])).length;

  const copyDown = () => {
    const text = classDraft.comment.trim();
    if (!text) return;
    let copied = 0;
    setStudents((prev) => {
      const next = { ...prev };
      for (const s of roster) {
        // Only EMPTY comments are filled; a student who already has words
        // written about them is never overwritten. The shared box keeps its
        // own text (port guide §2.5).
        if (!(next[s.studentId]?.comment ?? '').trim()) {
          next[s.studentId] = { ...(next[s.studentId] ?? emptyDraft()), comment: text };
          copied += 1;
        }
      }
      return next;
    });
    toast.success(t('notes.class.copiedToast', { count: copied }));
  };

  const submit = () => {
    // `studentNotes` always sends every roster row: an untouched student's
    // draft equals its baseline exactly (blank if they never had a row,
    // unchanged text if they did), and both cases are a safe no-op on the
    // API (packages/validation/src/progress.ts + services/progressNotes.ts —
    // a blank pair with no existing row deletes nothing; a non-blank pair
    // re-saves the same content it already had).
    //
    // `classNote` is different: an OMITTED key means "leave that tier
    // alone", while a PRESENT key with blank strings means "clear it" and
    // deletes the row. Sending blanks whenever the class tier is merely
    // HIDDEN (roster shrank to one student) would silently wipe a class note
    // saved before the roster changed — so the key is only included when the
    // tier is actually shown.
    const studentItems = template?.studentItems ?? [];
    const homeworkItems = template?.homeworkItems ?? [];
    const payload: PutSessionNotesInput = {
      studentNotes: roster.map((s) => {
        const d = students[s.studentId] ?? emptyDraft();
        return {
          studentId: s.studentId,
          comment: d.comment,
          homework: d.homework,
          items: toWireItems(d.items, studentItems, baseline.orphansByStudent[s.studentId] ?? []),
          homeworkItems: toWireItems(d.homeworkItems, homeworkItems, baseline.homeworkOrphansByStudent[s.studentId] ?? []),
          homeworkPhotos: toWirePhotos(d.photos),
          // THI-346: always sent (null clears), the same every-row contract
          // as comment/homework; the API ignores it for a subject with the
          // switch off only in what it renders, never in what it stores.
          rating: d.rating,
        };
      }),
    };
    if (showClassTier) {
      payload.classNote = {
        comment: classDraft.comment, homework: classDraft.homework,
        items: toWireItems(classDraft.items, template?.classItems ?? [], baseline.classOrphans),
        homeworkItems: toWireItems(classDraft.homeworkItems, homeworkItems, baseline.classHomeworkOrphans),
        homeworkPhotos: toWirePhotos(classDraft.photos),
      };
    }
    save.mutate(payload, {
      onSuccess: () => {
        onOpenChange(false);
        toast.success(t('notes.savedToast', { date: fmtDayLabel(session.startAt) }));
      },
    });
  };

  const body = notes ? (
    <SessionNotesForm
      roster={roster}
      showClassTier={showClassTier}
      template={template}
      ratingEnabled={notes?.ratingEnabled ?? false}
      classDraft={classDraft}
      onClassCommentChange={(value) => setClassDraft((d) => ({ ...d, comment: value }))}
      onClassHomeworkChange={(value) => setClassDraft((d) => ({ ...d, homework: value }))}
      onClassItemChange={(itemId, value) => setClassDraft((d) => ({ ...d, items: { ...d.items, [itemId]: value } }))}
      onClassHomeworkItemChange={(itemId, value) => setClassDraft((d) => ({ ...d, homeworkItems: { ...d.homeworkItems, [itemId]: value } }))}
      onCopyDown={copyDown}
      students={students}
      onStudentCommentChange={(studentId, value) =>
        setStudents((prev) => ({ ...prev, [studentId]: { ...(prev[studentId] ?? emptyDraft()), comment: value } }))
      }
      onStudentHomeworkChange={(studentId, value) =>
        setStudents((prev) => ({ ...prev, [studentId]: { ...(prev[studentId] ?? emptyDraft()), homework: value } }))
      }
      onStudentRatingChange={(studentId, value) =>
        setStudents((prev) => ({ ...prev, [studentId]: { ...(prev[studentId] ?? emptyDraft()), rating: value } }))
      }
      onStudentItemChange={(studentId, itemId, value) =>
        setStudents((prev) => {
          const cur = prev[studentId] ?? emptyDraft();
          return { ...prev, [studentId]: { ...cur, items: { ...cur.items, [itemId]: value } } };
        })
      }
      onStudentHomeworkItemChange={(studentId, itemId, value) =>
        setStudents((prev) => {
          const cur = prev[studentId] ?? emptyDraft();
          return { ...prev, [studentId]: { ...cur, homeworkItems: { ...cur.homeworkItems, [itemId]: value } } };
        })
      }
      orphansByStudent={baseline.orphansByStudent}
      homeworkOrphansByStudent={baseline.homeworkOrphansByStudent}
      classOrphans={baseline.classOrphans}
      classHomeworkOrphans={baseline.classHomeworkOrphans}
      sessionId={session.id}
      photoSubtitle={`${session.subject.name} · ${fmtDayLabel(session.startAt)}`}
      planAllowsPhotos={planAllowsPhotos}
      onClassPhotosChange={(next) => setClassDraft((d) => ({ ...d, photos: next }))}
      onStudentPhotosChange={(studentId, next) =>
        setStudents((prev) => ({ ...prev, [studentId]: { ...(prev[studentId] ?? emptyDraft()), photos: next } }))
      }
      openStudentId={openStudentId}
      onToggleStudent={(studentId) => setOpenStudentId((cur) => (cur === studentId ? null : studentId))}
      focusStudentId={focusStudentId}
      focusRef={focusRef}
    />
  ) : null;

  // Same summary formula the old SessionNotesForm footer used — now built
  // here because the footer itself moved into the panel (D1 step 1).
  const summaryLine = [
    hasAnyText(classDraft) ? t('notes.entry.summaryClass') : null,
    writtenCount > 0 ? t('notes.entry.summaryStudents', { count: writtenCount }) : null,
    hasAnyHomework(classDraft) ? t('notes.entry.summaryHomework') : null,
    totalPhotos > 0 ? t('notes.photos.count', { n: totalPhotos }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const footer = (
    // The hint sits ABOVE the button row, never under it: the last strip of
    // a bottom drawer is the phone's home-indicator gesture zone, and a link
    // placed there could not be tapped (thầy's report, 2026-09-04). The row
    // itself keeps a safe-area inset below it for the same reason.
    <div className="shrink-0 border-t border-surface-200 bg-surface-0 pb-[env(safe-area-inset-bottom)]">
      {/* Spec §13.3: nudge managers to create a template; hidden once one exists. */}
      {notes && template === null && <TemplateHintLine subjectId={session.subject.id} />}
      <div className="flex flex-row items-center gap-2.5 px-5 pb-4.5 pt-3">
        <span className="min-w-0 flex-1 truncate text-[12px] text-surface-500">{summaryLine}</span>
        <NotesFooterCancelButton disabled={save.isPending} />
        <Button className="h-[44px] px-6" onClick={submit} disabled={save.isPending || uploading}>
          {uploading ? t('notes.photos.uploading') : t('notes.save')}
        </Button>
      </div>
    </div>
  );

  return (
    <ResponsiveSheet
      open={open}
      onOpenChange={onOpenChange}
      a11yTitle={t('notes.entry.title')}
      title={t('notes.entry.title')}
      description={t('notes.header.meta', {
        subject: session.subject.name,
        day: fmtDayLabel(session.startAt),
        roster: roster.length,
        present: roster.filter((r) => r.attendance === 'PRESENT').length,
      })}
      dirty={dirty}
      discard={{
        title: t('notes.discardTitle'),
        body: t('notes.discardBody'),
        confirm: t('notes.discardConfirm'),
        cancel: t('notes.discardCancel'),
      }}
      footer={footer}
    >
      {body}
    </ResponsiveSheet>
  );
}
